/**
 * Camunda 7.18 Job REST API Service
 * https://docs.camunda.org/manual/7.18/reference/rest/job/
 */

import { CamundaApiClient, getCamundaApiClient } from '../CamundaApiClient';
import type {
  Job,
  JobQuery,
  JobDefinition,
  JobDefinitionQuery,
  CountResult,
  JobDueDateDto,
  JobSuspensionStateDto,
  SetJobRetriesAsyncDto,
  Batch,
} from '../types';

export class JobService {
  private client: CamundaApiClient;
  private basePath = '/engine-rest/job';
  private definitionBasePath = '/engine-rest/job-definition';

  constructor(client?: CamundaApiClient) {
    this.client = client || getCamundaApiClient();
  }

  // ============================================================================
  // Get Jobs
  // ============================================================================

  /**
   * Get a list of jobs
   */
  async getList(query?: JobQuery): Promise<Job[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<Job[]>(`${this.basePath}${params}`);
  }

  /**
   * Get jobs count
   */
  async getCount(query?: JobQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/count${params}`);
  }

  /**
   * Query jobs (POST)
   */
  async queryList(query: JobQuery): Promise<Job[]> {
    const { firstResult, maxResults, sortBy, sortOrder, ...body } = query;
    const params = this.buildPaginationParams({ firstResult, maxResults, sortBy, sortOrder });
    return this.client.post<Job[]>(`${this.basePath}${params}`, body);
  }

  /**
   * Query jobs count (POST)
   */
  async queryCount(query: JobQuery): Promise<CountResult> {
    return this.client.post<CountResult>(`${this.basePath}/count`, query);
  }

  // ============================================================================
  // Get Single Job
  // ============================================================================

  /**
   * Get job by ID
   */
  async getById(id: string): Promise<Job> {
    return this.client.get<Job>(`${this.basePath}/${id}`);
  }

  /**
   * Get job stacktrace
   */
  async getStacktrace(id: string): Promise<string> {
    return this.client.getText(`${this.basePath}/${id}/stacktrace`);
  }

  // ============================================================================
  // Execute Job
  // ============================================================================

  /**
   * Execute a job
   */
  async execute(id: string): Promise<void> {
    await this.client.post<void>(`${this.basePath}/${id}/execute`, {});
  }

  // ============================================================================
  // Retries
  // ============================================================================

  /**
   * Set job retries
   */
  async setRetries(id: string, retries: number): Promise<void> {
    await this.client.put<void>(`${this.basePath}/${id}/retries`, { retries });
  }

  /**
   * Set retries for multiple jobs
   */
  async setRetriesSync(dto: {
    jobIds?: string[];
    jobQuery?: JobQuery;
    retries: number;
  }): Promise<void> {
    await this.client.post<void>(`${this.basePath}/retries`, dto);
  }

  /**
   * Set retries async
   */
  async setRetriesAsync(dto: SetJobRetriesAsyncDto): Promise<Batch> {
    return this.client.post<Batch>(`${this.basePath}/retries-async`, dto);
  }

  // ============================================================================
  // Due Date
  // ============================================================================

  /**
   * Set job due date
   */
  async setDueDate(id: string, dto: JobDueDateDto): Promise<void> {
    await this.client.put<void>(`${this.basePath}/${id}/duedate`, dto);
  }

  /**
   * Recalculate job due date
   */
  async recalculateDueDate(
    id: string,
    options?: { creationDateBased?: boolean }
  ): Promise<void> {
    const params = options?.creationDateBased !== undefined
      ? `?creationDateBased=${options.creationDateBased}`
      : '';
    await this.client.post<void>(`${this.basePath}/${id}/duedate/recalculate${params}`, {});
  }

  // ============================================================================
  // Priority
  // ============================================================================

  /**
   * Set job priority
   */
  async setPriority(id: string, priority: number): Promise<void> {
    await this.client.put<void>(`${this.basePath}/${id}/priority`, { priority });
  }

  // ============================================================================
  // Suspension
  // ============================================================================

  /**
   * Activate/Suspend job by ID
   */
  async updateSuspensionStateById(id: string, suspended: boolean): Promise<void> {
    await this.client.put<void>(`${this.basePath}/${id}/suspended`, { suspended });
  }

  /**
   * Activate/Suspend jobs
   */
  async updateSuspensionState(dto: JobSuspensionStateDto): Promise<void> {
    await this.client.put<void>(`${this.basePath}/suspended`, dto);
  }

  // ============================================================================
  // Delete
  // ============================================================================

  /**
   * Delete a job
   */
  async delete(id: string): Promise<void> {
    await this.client.delete<void>(`${this.basePath}/${id}`);
  }

  // ============================================================================
  // Job Definitions
  // ============================================================================

  /**
   * Get job definitions
   */
  async getDefinitions(query?: JobDefinitionQuery): Promise<JobDefinition[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<JobDefinition[]>(`${this.definitionBasePath}${params}`);
  }

  /**
   * Get job definitions count
   */
  async getDefinitionsCount(query?: JobDefinitionQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.definitionBasePath}/count${params}`);
  }

  /**
   * Query job definitions (POST)
   */
  async queryDefinitions(query: JobDefinitionQuery): Promise<JobDefinition[]> {
    const { firstResult, maxResults, sortBy, sortOrder, ...body } = query;
    const params = this.buildPaginationParams({ firstResult, maxResults, sortBy, sortOrder });
    return this.client.post<JobDefinition[]>(`${this.definitionBasePath}${params}`, body);
  }

  /**
   * Get job definition by ID
   */
  async getDefinitionById(id: string): Promise<JobDefinition> {
    return this.client.get<JobDefinition>(`${this.definitionBasePath}/${id}`);
  }

  /**
   * Set job definition priority
   */
  async setDefinitionPriority(
    id: string,
    priority: number | null,
    includeJobs?: boolean
  ): Promise<void> {
    await this.client.put<void>(`${this.definitionBasePath}/${id}/jobPriority`, {
      priority,
      includeJobs,
    });
  }

  /**
   * Activate/Suspend job definition by ID
   */
  async updateDefinitionSuspensionStateById(
    id: string,
    suspended: boolean,
    options?: {
      includeJobs?: boolean;
      executionDate?: string;
    }
  ): Promise<void> {
    await this.client.put<void>(`${this.definitionBasePath}/${id}/suspended`, {
      suspended,
      ...options,
    });
  }

  /**
   * Activate/Suspend job definitions
   */
  async updateDefinitionSuspensionState(dto: {
    processDefinitionId?: string;
    processDefinitionKey?: string;
    processDefinitionTenantId?: string;
    processDefinitionWithoutTenantId?: boolean;
    suspended: boolean;
    includeJobs?: boolean;
    executionDate?: string;
  }): Promise<void> {
    await this.client.put<void>(`${this.definitionBasePath}/suspended`, dto);
  }

  /**
   * Set retries for job definition
   */
  async setDefinitionRetries(id: string, retries: number): Promise<void> {
    await this.client.post<void>(`${this.definitionBasePath}/${id}/retries`, { retries });
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private buildQueryParams(query?: Record<string, any>): string {
    if (!query) return '';
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          params.append(key, value.join(','));
        } else if (typeof value === 'object') {
          params.append(key, JSON.stringify(value));
        } else {
          params.append(key, String(value));
        }
      }
    });
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }

  private buildPaginationParams(params: {
    firstResult?: number;
    maxResults?: number;
    sortBy?: string;
    sortOrder?: string;
  }): string {
    const urlParams = new URLSearchParams();
    if (params.firstResult !== undefined) urlParams.append('firstResult', String(params.firstResult));
    if (params.maxResults !== undefined) urlParams.append('maxResults', String(params.maxResults));
    if (params.sortBy) urlParams.append('sortBy', params.sortBy);
    if (params.sortOrder) urlParams.append('sortOrder', params.sortOrder);
    const queryString = urlParams.toString();
    return queryString ? `?${queryString}` : '';
  }
}

// Singleton instance
let jobServiceInstance: JobService | null = null;

export function getJobService(): JobService {
  if (!jobServiceInstance) {
    jobServiceInstance = new JobService();
  }
  return jobServiceInstance;
}

export default JobService;
